import React from 'react';
import DemoStage from '../DemoStage.jsx';

/**
 * Animated tour of the sidebar: switch pages, collapse to icons-only with the
 * chevron (hover tooltip shown), expand again. Mirrors the real Layout.jsx
 * chrome — nav items borrow the app's own .nav-item / .nav-label classes; the
 * collapsed tooltip mimics the `data-label` ::after pill from global.css.
 */

const STEPS = [
  {
    caption: 'Pages live in the sidebar — the one you are on is highlighted.',
    scene: 'dash', ms: 2200,
    cursor: 'nav-cases',
  },
  {
    caption: 'Click an item to move between pages.',
    scene: 'cases', ms: 2000,
    cursor: 'nav-cases', click: true,
  },
  {
    caption: 'Need more room? The chevron on the sidebar edge collapses it…',
    scene: 'cases', ms: 1800,
    cursor: 'collapse',
  },
  {
    caption: '…down to icons only — hover an icon to see its name.',
    scene: 'collapsed', ms: 2600,
    cursor: 'collapse', click: true,
  },
  {
    caption: 'Click again to expand. Your choice is remembered on this device.',
    scene: 'expanded', ms: 2600,
    cursor: 'collapse', click: true,
  },
];

// The three Overview icons, copied from Layout.jsx for fidelity.
const ICONS = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  cases: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  ),
  knowledge: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>
  ),
};

function NavItem({ icon, label, active, collapsed, target, tooltip }) {
  return (
    <div
      className={`nav-item${active ? ' active' : ''}`}
      data-kb-target={target}
      style={{
        position: 'relative',
        justifyContent: collapsed ? 'center' : undefined,
        transition: 'background 200ms var(--ease)',
      }}
    >
      {icon}
      {!collapsed && <span className="nav-label">{label}</span>}
      {collapsed && tooltip && (
        <span
          className="kb-pop"
          style={{
            position: 'absolute', left: 'calc(100% + 10px)', top: '50%',
            transform: 'translateY(-50%)',
            background: 'var(--text)', color: 'var(--surface)',
            fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
            padding: '4px 8px', borderRadius: 6,
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}

export default function SidebarDemo() {
  return (
    <DemoStage steps={STEPS} label="The sidebar" height={200}>
      {(step) => {
        const scene = step?.scene ?? 'dash';
        const collapsed = scene === 'collapsed';
        const activeItem = scene === 'dash' ? 'dashboard' : 'cases';
        return (
          <div className="kb-mock" style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
            {/* Mini sidebar */}
            <div style={{
              position: 'relative', flexShrink: 0,
              width: collapsed ? 54 : 176,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 12, padding: '10px 8px',
              display: 'flex', flexDirection: 'column', gap: 2,
              transition: 'width 300ms var(--ease)',
            }}>
              {/* Brand row */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                justifyContent: collapsed ? 'center' : 'flex-start',
                padding: '2px 8px 8px',
              }}>
                <span style={{
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                  width: 22, height: 22, borderRadius: 6,
                  background: 'var(--brand)', color: '#fff',
                  fontSize: 9, fontWeight: 700, letterSpacing: '0.02em',
                }}>
                  AE
                </span>
                {!collapsed && <strong style={{ fontSize: 12.5, color: 'var(--text)' }}>AE Track</strong>}
              </div>

              {!collapsed && <div className="nav-section-label">Overview</div>}
              <NavItem icon={ICONS.dashboard} label="Dashboard" active={activeItem === 'dashboard'} collapsed={collapsed} />
              <NavItem icon={ICONS.cases} label="All Cases" active={activeItem === 'cases'} collapsed={collapsed} target="nav-cases" tooltip />
              <NavItem icon={ICONS.knowledge} label="Knowledge" collapsed={collapsed} />

              {/* Collapse chevron — sits on the sidebar edge like the real one */}
              <button
                type="button"
                tabIndex={-1}
                data-kb-target="collapse"
                aria-hidden="true"
                style={{
                  position: 'absolute', top: 12, right: -10,
                  display: 'grid', placeItems: 'center',
                  width: 20, height: 20, borderRadius: '50%',
                  background: 'var(--surface)',
                  border: '1px solid var(--border-strong)',
                  color: 'var(--text-3)', cursor: 'pointer', padding: 0,
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
                  {collapsed
                    ? <polyline points="9 18 15 12 9 6"/>
                    : <polyline points="15 18 9 12 15 6"/>}
                </svg>
              </button>
            </div>

            {/* Ghost of the main content area — just context */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6 }}>
              <span className="kb-skel" style={{ width: '45%', height: 16 }} />
              <span className="kb-skel" style={{ width: '80%' }} />
              <span className="kb-skel" style={{ width: '70%' }} />
              <span className="kb-skel" style={{ width: '76%' }} />
            </div>
          </div>
        );
      }}
    </DemoStage>
  );
}
